/**
 * Custom Parallax System
 *
 * Web component <custom-parallax> with scroll-driven effects.
 *
 * Usage:
 *   <custom-parallax data-parallax-type="zoom" data-parallax-intensity="30">
 *     <img src="..." />
 *   </custom-parallax>
 *
 * Types:
 * - zoom: Scale from 1.0 to 1.0 + intensity/1000 (e.g. 30 = 1.03)
 * - card-scroll: translateY separation based on scroll position
 * - offset: Horizontal shift based on scroll
 *
 * Attributes:
 * - data-parallax-type: "zoom" | "card-scroll" | "offset"
 * - data-parallax-intensity: 0-100 (default: 30)
 * - data-parallax-disable-mobile: present = disabled below 768px
 */

(function () {
  'use strict';

  class CustomParallaxElement extends HTMLElement {
    constructor() {
      super();
      this._ticking = false;
      this._onScroll = this._onScroll.bind(this);
    }

    connectedCallback() {
      this._type = this.dataset.parallaxType || 'zoom';
      this._intensity = parseInt(this.dataset.parallaxIntensity || '30', 10);
      this._disableMobile = this.hasAttribute('data-parallax-disable-mobile');

      // Respect reduced motion
      if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) return;

      // Disable on mobile if specified
      if (this._disableMobile && window.innerWidth < 768) return;

      // Set overflow hidden for zoom type
      if (this._type === 'zoom') {
        this.style.overflow = 'hidden';
        this.style.display = 'block';
      }

      window.addEventListener('scroll', this._onScroll, { passive: true });
      this._onScroll();
    }

    disconnectedCallback() {
      window.removeEventListener('scroll', this._onScroll);
    }

    _onScroll() {
      if (this._ticking) return;
      this._ticking = true;

      requestAnimationFrame(function () {
        this._update();
        this._ticking = false;
      }.bind(this));
    }

    _update() {
      var rect = this.getBoundingClientRect();
      var windowH = window.innerHeight;

      // Element not in extended viewport - skip
      if (rect.bottom < -windowH || rect.top > windowH * 2) return;

      // Scroll ratio: 0 when element enters bottom, 1 when exits top
      var ratio = 1 - (rect.bottom / (windowH + rect.height));
      ratio = Math.max(0, Math.min(1, ratio));

      var target = this.firstElementChild || this;

      switch (this._type) {
        case 'zoom': {
          var maxScale = 1 + this._intensity / 1000;
          var scale = 1 + (maxScale - 1) * ratio;
          target.style.transform = 'scale(' + scale.toFixed(4) + ')';
          target.style.transformOrigin = 'center center';
          target.style.willChange = 'transform';
          break;
        }
        case 'card-scroll': {
          var maxShift = this._intensity * 0.5;
          var shift = (ratio - 0.5) * maxShift;
          target.style.transform = 'translateY(' + shift.toFixed(2) + 'px)';
          target.style.willChange = 'transform';
          break;
        }
        case 'offset': {
          var maxOffset = this._intensity * 0.3;
          var offset = (ratio - 0.5) * maxOffset;
          target.style.transform = 'translateX(' + offset.toFixed(2) + 'px)';
          target.style.willChange = 'transform';
          break;
        }
      }
    }
  }

  if (!customElements.get('custom-parallax')) {
    customElements.define('custom-parallax', CustomParallaxElement);
  }
})();
